/* Copyright (c) 2020-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { from, Subject, Observable, of, merge } from 'rxjs';
import { mergeMap, filter, catchError, mapTo, map, take, switchMap, shareReplay } from 'rxjs/operators';
import { LoggingService, Logger } from 'crimson/lib/logging/logging.service';
import { GfnCacheManifest } from 'service-workers/gfn/gfn-sw.types';
import {
    SwRuntimeCacheUpdatedNotification,
    SwNotificationTypes,
    SwUpdateEvent,
    SwUpdateState,
    SwUpdateResult,
    SwMessageType,
    SwActivatedEvent,
    PostMessageData,
    SwFetchSucceededParams,
    SwRuntimeCacheUpdatedPayload,
    CommonHeaderNames
} from '../../../../service-workers/src/sw.types';
import { SWRegistrationInfo } from './service-worker.type';

@Injectable({
    providedIn: 'root'
})
export class ServiceWorkerService {
    private readonly logger: Logger;
    private readonly swCacheUpdate$: Subject<SwRuntimeCacheUpdatedNotification>;
    private readonly networkFetchSucceeded$: Subject<SwFetchSucceededParams>;
    private readonly swActivated$: Subject<SwActivatedEvent>;
    private readonly swUpdateReady$: Subject<SwUpdateEvent>;
    private readonly resetSwUpdateCache$: Subject<null>;
    private readonly swNotifyListener$: Subject<PostMessageData>;
    private readonly swPushNotificationListener$: Subject<PostMessageData>;
    private readonly swPushNotificationTelemetryListener$: Subject<PostMessageData>;
    private readonly swPushNotificationTracing: Subject<PostMessageData>;
    private readonly swPushNotificationReceivedListener$: Subject<PostMessageData>;
    private readonly swUpdateReadyCached$: Observable<SwUpdateEvent>;
    private readonly swSharedStorageUpdate = new Subject<PostMessageData>();
    private readonly swLoggingUpdate = new Subject<PostMessageData>();
    private readonly swActivationRequested$ = new Subject<null>();
    private readonly swClientVersion = new Subject<string>();

    constructor(loggingService: LoggingService) {
        this.logger = loggingService.getLogger('libraries/service-worker.service');
        this.swCacheUpdate$ = new Subject();
        this.networkFetchSucceeded$ = new Subject<SwFetchSucceededParams>();
        this.swActivated$ = new Subject();
        this.swUpdateReady$ = new Subject();
        this.resetSwUpdateCache$ = new Subject();
        this.swNotifyListener$ = new Subject();
        this.swPushNotificationListener$ = new Subject();
        this.swPushNotificationTelemetryListener$ = new Subject();
        this.swPushNotificationTracing = new Subject();
        this.swPushNotificationReceivedListener$ = new Subject();
        this.swUpdateReadyCached$ = merge(this.swUpdateReady$.asObservable(), this.resetSwUpdateCache$).pipe(
            shareReplay(1)
        );
    }

    get swCacheUpdated(): Observable<SwRuntimeCacheUpdatedNotification> {
        return this.swCacheUpdate$.asObservable();
    }

    get networkResponseReceived(): Observable<SwFetchSucceededParams> {
        return this.networkFetchSucceeded$.asObservable();
    }

    /**
     * Emits when a **new version** of Service Worker has been installed & waiting to be activated
     */
    get swUpdateReady(): Observable<SwUpdateEvent> {
        return this.swUpdateReady$.asObservable();
    }

    /**
     * Same as `swUpdateReady` but also returns the last cached update event
     * Useful for lazy-loaded components/services
     */
    get swUpdateReadyCached(): Observable<SwUpdateEvent> {
        return this.swUpdateReadyCached$;
    }

    /**
     * Emits when a **new version** of Service Worker is activated
     */
    get swActivated(): Observable<SwActivatedEvent> {
        return this.swActivated$.asObservable();
    }

    /**
     * Emits message to its listeners
     */
    get swNotifyListener(): Observable<PostMessageData> {
        return this.swNotifyListener$.asObservable();
    }

    get swPushNotificationListener(): Observable<PostMessageData> {
        return this.swPushNotificationListener$.asObservable();
    }

    get swPushNotificationTelemetryListener(): Observable<PostMessageData> {
        return this.swPushNotificationTelemetryListener$.asObservable();
    }

    get swPushNotificationTracingListener(): Observable<PostMessageData> {
        return this.swPushNotificationTracing.asObservable();
    }

    get swPushNotificationReceivedListener(): Observable<PostMessageData> {
        return this.swPushNotificationReceivedListener$.asObservable();
    }

    get swSharedStorageUpdate$(): Observable<PostMessageData> {
        return this.swSharedStorageUpdate.asObservable();
    }

    get swLoggingUpdate$(): Observable<PostMessageData> {
        return this.swLoggingUpdate.asObservable();
    }

    get swAssociatedClientVersion(): Observable<string> {
        return this.swClientVersion.asObservable();
    }

    initialize(): void {
        if (this.serviceWorkerSupported()) {
            this.swHandleMessages();
            this.swHandleControllerChanges();
            this.swHandleUpdates();
        } else {
            this.logger.info('Service worker not supported');
        }
    }

    willGFNServiceWorkerHandleRequest(requestURL: string, bypassCacheHeaderValue: string): boolean {
        if (bypassCacheHeaderValue && bypassCacheHeaderValue === JSON.stringify(true)) {
            return false;
        }

        for (const key in GfnCacheManifest.staleWhileRevalidate) {
            if (GfnCacheManifest.staleWhileRevalidate.hasOwnProperty(key)) {
                const entry = GfnCacheManifest.staleWhileRevalidate[key];
                if (entry.routePattern.test(requestURL)) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Checks if browser supports service workers
     */
    serviceWorkerSupported(): boolean {
        return 'serviceWorker' in navigator;
    }

    /**
     * Provides service worker registration info if it supported
     * otherwise sends null
     */
    getSWRegistrationInfo(): SWRegistrationInfo {
        if (this.serviceWorkerSupported()) {
            const swRegistrationInfo: SWRegistrationInfo = {
                isSuccess: (window as any)?.SWRegistrationInfo?.isSuccess,
                error: (window as any)?.SWRegistrationInfo?.error
            };
            return swRegistrationInfo;
        }
        return null;
    }

    /**
     * Clears service worker update ready cache - `swUpdateReadyCached`.
     *
     * First subscription to `swUpdateReadyCached` after invoking this function will emit null.
     */
    clearSwUpdateCache() {
        // Cannot do this.swUpdateReady$.next(null) because other observables rely on it and may cause unexpected issues
        // instead we have another observable that will reset only the observable that is caching the result.
        this.logger.info('SW Update Cache: Cleared');
        this.resetSwUpdateCache$.next(null);
    }

    /**
     * Check for service worker update
     *
     * - If a new service worker is installing/installed, it will return `updateAvailable: true`
     * - Else it will make an HTTP call to the service worker's URL to check for an update.
     */
    checkForUpdates(): Promise<SwUpdateResult> {
        if (!this.serviceWorkerSupported()) {
            return Promise.reject('SW Update Check: Browser does not support service workers');
        }

        return navigator.serviceWorker
            .getRegistration()
            .then(reg => {
                if (!reg) {
                    return Promise.reject('Registration does not exist');
                }
                const hasUpdateAvailable = this.swHasUpdate(reg);
                if (hasUpdateAvailable) {
                    this.logger.info(
                        `Update available. SW in installing state = ${reg.installing}, SW in waiting state = ${reg.waiting}`
                    );
                    return reg;
                }
                this.logger.info('Explicitly checking for SW update at same location');
                return reg.update() as any as Promise<ServiceWorkerRegistration>;
            })
            .then((reg: ServiceWorkerRegistration) => {
                const response = {
                    updateAvailable: this.swHasUpdate(reg),
                    error: null
                };
                this.logger.info(`SW updateAvailable: ${response.updateAvailable}`);
                return response;
            })
            .catch(err => {
                this.logger.error('SW Update Check: Error', err.toString());
                return Promise.resolve({
                    updateAvailable: false,
                    error: err
                });
            });
    }

    /**
     * Request the new service worker to be activated.
     *
     * Will wait for the new service worker to be installed first and then send `SKIP_WAITING` message.
     * Service worker should handle the `SKIP_WAITING` and invoke `self.skipWaiting()`
     */
    activateServiceWorker(): Observable<boolean> {
        if (!this.serviceWorkerSupported()) {
            this.logger.info('SW Activate: Browser does not support service workers');
            return of(false);
        }

        return from(navigator.serviceWorker.getRegistration()).pipe(
            switchMap((reg: ServiceWorkerRegistration) => {
                if (!reg) {
                    this.logger.info('SW Activate: No service worker registration');
                    return of(null);
                } else if (!this.swHasUpdate(reg)) {
                    this.logger.info('SW Activate: Nothing to activate');
                    return of(null);
                } else if (reg.installing) {
                    this.logger.info('SW Activate: Waiting for new service worker to be installed');
                    return this.swUpdateReady.pipe(mapTo(reg), take(1));
                } else {
                    return of(reg);
                }
            }),
            map((reg: ServiceWorkerRegistration) => {
                if (reg?.waiting) {
                    this.logger.info('SW Activate: Requested to activate.');
                    reg.waiting.postMessage({ type: SwMessageType.SkipWaiting });
                    this.swActivationRequested$.next();
                    return true;
                }
                this.logger.info('SW Activate: No service worker waiting.');
                return false;
            }),
            take(1)
        );
    }

    get swActivationRequested(): Observable<null> {
        return this.swActivationRequested$.asObservable();
    }

    postMessageSw(data: PostMessageData) {
        if (!this.serviceWorkerSupported()) {
            this.logger.info('SW PostMsg: Browser does not support service workers');
            return;
        }
        navigator.serviceWorker.ready.then((reg: ServiceWorkerRegistration) => {
            // SharedStorageUpdate messages are noisy, but other logs are
            // essential for debugging, hence kept at INFO level.
            if (data.type === SwMessageType.SharedStorageUpdate) {
                this.logger.verbose('Calling SW postMessage with: ', data);
            } else {
                this.logger.info('Calling SW postMessage with: ', data);
            }
            reg.active?.postMessage({
                type: data.type,
                content: data.content
            });
        });
    }

    /**
     * Checks if the service worker registration has a new service worker in installing/installed state
     */
    private swHasUpdate(reg: ServiceWorkerRegistration): boolean {
        return !!(reg.installing || reg.waiting);
    }

    /**
     * Handles message events from service worker.
     */
    private swHandleMessages() {
        navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
            if (event.data.type === SwNotificationTypes.RuntimeCacheUpdated) {
                this.logger.info('CacheUpdated event received from Service Worker: ', event.data.payload?.requestUrl);
                const { cacheName, requestUrl, noDataUpdate } = event.data.payload as SwRuntimeCacheUpdatedPayload;
                from(caches.open(cacheName))
                    .pipe(
                        mergeMap(cache => cache.match(requestUrl)),
                        filter(response => !!response),
                        mergeMap(updatedResponse => updatedResponse.json()),
                        catchError(e => {
                            this.logger.info('error while checking for cache update', e.toString());
                            return of(null);
                        })
                    )
                    .subscribe(jsonResponse => {
                        if (jsonResponse) {
                            this.logger.info('SW cache updated', cacheName);
                            this.swCacheUpdate$.next({
                                name: cacheName,
                                url: requestUrl,
                                data: jsonResponse,
                                noDataUpdate
                            });
                        }
                    });
            } else if (event?.data.type === SwMessageType.CloseStreamerSession) {
                this.logger.info('Msg received from another tab: ', event.data.type);
                this.swNotifyListener$.next(event.data);
            } else if (event?.data.type === SwMessageType.PushNotification) {
                this.logger.info('Firebase Token received from Service Worker: ', event.data.type);
                this.swPushNotificationListener$.next(event.data);
            } else if (event?.data.type === SwMessageType.PushNotificationTelemetry) {
                this.logger.info('Telemetry Event received from Service Worker: ', event.data.type);
                this.swPushNotificationTelemetryListener$.next(event.data);
            } else if (event?.data.type === SwMessageType.PushNotificationTracing) {
                this.logger.info('Telemetry Event received from Service Worker: ', event.data.type);
                this.swPushNotificationTracing.next(event.data);
            } else if (event?.data.type === SwMessageType.PushNotificationReceived) {
                this.logger.info('PushNotificationReceived event received from Service Worker: ', event.data.type);
                this.swPushNotificationReceivedListener$.next(event.data);
            } else if (event?.data.type === SwMessageType.SharedStorageUpdate) {
                this.logger.info('SharedStorageUpdate event received from Service Worker: ', event.data.type);
                this.swSharedStorageUpdate.next(event.data);
            } else if (event?.data.type === SwNotificationTypes.FetchSuccess) {
                this.logger.info('FetchSuccess event received from Service Worker: ', event.data.payload?.requestUrl);
                this.networkFetchSucceeded$.next(event.data.payload);
            } else if (event?.data.type === SwMessageType.LoggerNotification) {
                this.logger.info('Logger event received from Service Worker: ', event.data.type);
                this.swLoggingUpdate.next(event.data);
            } else if (event?.data.type === SwMessageType.GetClientVersion) {
                this.logger.info('Received client version is ', event.data);
                this.swClientVersion.next(event.data.content);
            } else {
                // Logging unhandled/unknown events
                this.logger.info('Unhandled event received from Service Worker ', event.data);
            }
        });
    }

    /**
     * Handles service worker updates.
     *
     * Notifies when the new service worker is installed using the `swUpdateEvents$` event.
     */
    private swHandleUpdates(): Promise<void> {
        if (!this.serviceWorkerSupported()) {
            return Promise.reject('SW Updates: Browser does not support service workers');
        }
        this.logger.info('SW Updates: Init');
        return navigator.serviceWorker
            .getRegistration()
            .then(reg => {
                if (!reg) {
                    return Promise.reject('SW Updates: Registration does not exist');
                }
                let hasActiveServiceWorker = this.swIsActive();
                this.logger.info(`SW Updates: Registration Exists, First Installation: ${!hasActiveServiceWorker}`);
                this.swOnUpdateInstalled(reg, (data: SwUpdateEvent) => {
                    this.logger.info('SW Updates: Update installed');
                    // notify only if a service worker exists
                    this.logger.info('SW Updates: Notifying');
                    data.firstInstallation = !hasActiveServiceWorker;
                    this.swUpdateReady$.next(data);

                    // SW Installed
                    hasActiveServiceWorker = true;
                });
                return Promise.resolve();
            })
            .catch(err => this.logger.error('SW Updates: Error', err.toString()));
    }

    /**
     * Wrapper function that implements all the events to detect service worker updates & installation.
     *
     * - Listens for `updatefound` event on the **current service worker**
     * - Listens for `statechange` event on the **new service worker**
     * - Invokes `callback(SwUpdateEvent)` whenever *the new service worker* is installed
     *
     * @param reg - Current service worker's registration
     * @param callback - Function that will be invoked when the new service worker is installed
     */
    private swOnUpdateInstalled(reg: ServiceWorkerRegistration, callback: (data: SwUpdateEvent) => void) {
        const callbackIfInstalled = newWorker => {
            if (newWorker.state === SwUpdateState.INSTALLED) {
                callback({ state: newWorker.state, ts: Date.now() });
            }
        };

        const listenForStateChange = (newWorker: ServiceWorker) => {
            if (!newWorker) {
                return;
            }
            this.logger.info('SW Update Listener: Update found');
            callbackIfInstalled(newWorker);
            newWorker.addEventListener('statechange', () => {
                this.logger.info('SW Update Listener: New worker state change:', newWorker.state);
                callbackIfInstalled(newWorker);
            });
        };

        this.logger.info(
            `SW registration state: active = ${reg.active}, waiting = ${reg.waiting}, installed = ${reg.installing}`
        );

        if (!reg) {
            this.logger.info('SW Update Listener: No service worker registration');
            return;
        } else if (reg.waiting) {
            this.logger.info('SW Update Listener: An update is ready');
            callback({ state: SwUpdateState.INSTALLED, ts: Date.now() });
        } else if (reg.installing) {
            listenForStateChange(reg.installing);
        }

        // if not already attached
        if (!reg.onupdatefound) {
            this.logger.info('SW Update Listener: Waiting for any updates to current worker');
            reg.addEventListener('updatefound', () => listenForStateChange(reg.installing));
        }
    }

    /**
     * Checks if any service worker is controlling the application
     */
    swIsActive(): boolean {
        return !!navigator.serviceWorker?.controller;
    }

    /**
     * Handles when a new service worker is activated and starts controlling the application.
     * Notifies using the `swActivated$` observable
     */
    private swHandleControllerChanges() {
        if (!this.serviceWorkerSupported() || navigator.serviceWorker.oncontrollerchange) {
            return;
        }
        this.logger.info('SW Ctrl Change: Init');
        let hasActiveServiceWorker = this.swIsActive();
        this.logger.info('SW Ctrl Change: has active service worker -', hasActiveServiceWorker);
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            this.logger.info('SW Ctrl Change: New service worker activated');
            this.swActivated$.next({
                firstInstallation: !hasActiveServiceWorker,
                ts: Date.now()
            });
            if (!hasActiveServiceWorker) {
                this.logger.info('SW Ctrl Change: First activation');
                hasActiveServiceWorker = true;
                return;
            }
        });
    }
}
